import React from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';

import { optionsHelper } from 'modules/common/helpers';

import { Col, Row } from 'modules/bootstrap/components';
import {
  TextInputRef,
  CountryInput,
  SelectController,
  Button,
} from 'modules/common/components';
import { StepDots } from 'modules/auth/components';

type Inputs = {
  name: string;
  email: string;
  surname: string;
  address: string;
  zip: string;
  city: string;
  country: SelectOption;
  gender: SelectOption;
};

export interface SignUpProfileFormProps {
  onSuccess: (profile) => void;
}

function SignUpProfileForm({ onSuccess }: SignUpProfileFormProps) {
  const { t } = useTranslation(['auth', 'common']);
  const {
    register,
    handleSubmit,
    control,
    setValue,
    getValues,

    formState: { errors },
  } = useForm<Inputs>();

  const onSubmit = (formData) => {
    const submitData = {
      ...formData,
      country: (formData.country && (formData.country.value as string)) || '',
      gender: (formData.gender && (formData.gender.value as string)) || '',
    } as UserProfileDTO;

    onSuccess(submitData);
  };

  const getErrorLabel = (fieldName, fieldLabel) => {
    return !!errors[fieldName] ? `${fieldLabel} ${t('common:required')}` : '';
  };

  const genderOptions = optionsHelper.getGenderOptions();

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="Form sm:t-p-2"
      data-testid="profile-form"
    >
      <Row>
        <Col xl={5}>
          <div className="Form-group">
            <label
              className={`Form-label ${errors.gender ? 'isErrored' : ''}`}
              htmlFor="gender"
            >
              {t('auth:title')}
            </label>

            <SelectController
              name="gender"
              options={genderOptions}
              control={control}
              rules={{ required: true }}
              defaultValue=""
              className={`Select ${errors.gender ? 'isErrored' : ''}`}
            />
            {errors.gender && (
              <div className="Form-alert">
                <span className="text-main-sm t-text-epsilon-600">
                  {getErrorLabel('gender', t('auth:title'))}
                </span>
              </div>
            )}
          </div>
        </Col>
      </Row>
      <Row>
        <Col xl={5}>
          <TextInputRef
            {...register('name', { required: true })}
            info={t('profile:name_info')}
            label={t('auth:name_f')}
            type="text"
            defaultValue=""
            error={getErrorLabel('name', t('auth:name_f'))}
          />
        </Col>

        <Col xl={7}>
          <TextInputRef
            {...register('surname', { required: true })}
            label={t('auth:name_l')}
            type="text"
            defaultValue=""
            error={getErrorLabel('surname', t('auth:name_l'))}
          />
        </Col>
      </Row>

      <Row>
        <Col xl={{ span: 8 }}>
          <TextInputRef
            {...register('address', { required: true })}
            info={t('profile:address_info')}
            label={t('auth:address_label')}
            type="text"
            error={getErrorLabel('address', t('auth:address_label'))}
            defaultValue=""
          />
        </Col>

        <Col xl={{ span: 4 }}>
          <TextInputRef
            {...register('zip', { required: true })}
            label={t('auth:postal_label')}
            type="text"
            error={getErrorLabel('zip', t('auth:postal_label'))}
            defaultValue=""
          />
        </Col>
      </Row>

      <Row>
        <Col xl={{ span: 8 }}>
          <TextInputRef
            {...register('city', { required: true })}
            label={t('auth:city_label')}
            type="text"
            error={getErrorLabel('city', t('auth:city_label'))}
            defaultValue=""
          />
        </Col>
      </Row>

      <Row>
        <Col xl={{ span: 8 }}>
          <CountryInput
            control={control}
            error={errors.country}
            defaultValue=""
            isRequired={true}
            refSetValue={setValue}
            refGetValues={getValues}
          />
        </Col>
      </Row>

      <div className="t-flex t-justify-between t-items-center lg:t-pt-5">
        <StepDots step={1} total={3} />
        <Button type="submit">{t('common:continue')}</Button>
      </div>
    </form>
  );
}

export default SignUpProfileForm;
